// State only from GET /weather/status; change-feed `weather_run` and window focus just trigger a reload.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { useAccessContext } from "@/contexts/AccessContext"
import { useEntityChangeSubscription } from "@/features/changeFeed"
import { getWeatherStatus } from "@/services/weatherApi"
import { isNewerRun, isValidRun } from "./weatherRunModel"

const WeatherRunContext = createContext(null)
const WEATHER_RUN_EVENTS = ["weather_run"]

export function WeatherRunProvider({ children }) {
  const { accessContext, setAccessContext } = useAccessContext()
  const accessRef = useRef({ accessContext, setAccessContext })
  accessRef.current = { accessContext, setAccessContext }

  const [status, setStatus] = useState(null)
  const [announcement, setAnnouncement] = useState(null)
  const knownRunRef = useRef(null)
  const inFlightRef = useRef(false)
  const pendingRef = useRef(false)

  const load = useCallback(async () => {
    if (!accessRef.current.accessContext?.token) return
    // A trigger during a running fetch must not get lost; it re-runs once afterwards.
    if (inFlightRef.current) {
      pendingRef.current = true
      return
    }
    inFlightRef.current = true
    do {
      pendingRef.current = false
      try {
        const next = await getWeatherStatus(accessRef.current.accessContext, accessRef.current.setAccessContext)
        if (next && isValidRun(next.activeRun)) {
          const known = knownRunRef.current
          knownRunRef.current = next.activeRun
          setStatus((prev) =>
            prev?.activeRun === next.activeRun && prev?.activatedAt === next.activatedAt ? prev : next,
          )
          if (isNewerRun(next.activeRun, known)) {
            setAnnouncement({ run: next.activeRun, previousRun: known })
          }
        }
      } catch (error) {
        console.warn("Weather status not available:", error)
      }
    } while (pendingRef.current)
    inFlightRef.current = false
  }, [])

  useEffect(() => {
    if (accessContext?.token) load()
  }, [accessContext?.token, load])

  useEntityChangeSubscription(WEATHER_RUN_EVENTS, load)

  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) load()
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => document.removeEventListener("visibilitychange", onVisible)
  }, [load])

  const value = useMemo(() => ({ status, announcement }), [status, announcement])
  return <WeatherRunContext.Provider value={value}>{children}</WeatherRunContext.Provider>
}

// null outside the provider (tests, previews): consumers fall back to age-based freshness.
export const useWeatherRun = () => useContext(WeatherRunContext)
